/**
 * Porte do parser de regra de limite do motor (`modules/limit_rule.py`).
 *
 * Gramática, idêntica à do motor:
 *
 * - `<=basica`            → LMI ≤ LMI da Básica
 * - `<=<frac>*basica`     → fração da Básica (ex.: `<=0.5*basica`)
 * - `<=value`             → LMI ≤ valor do equipamento
 * - `<=<frac>*value`      → fração do valor (ex.: `<=1.1*value`, a Básica de 110%)
 * - `max:<centavos>`      → teto absoluto
 * - cláusulas unidas por `&` → todas precisam valer
 *
 * Quem MANDA continua sendo o motor: ele revalida tudo e devolve
 * "I.S. acima do limite para esta cobertura." por slug. Este porte existe só
 * para antecipar o limite na tela — mostrar o teto ao lado do campo e barrar
 * o "Próximo" antes do PUT, em vez de o corretor descobrir por um 400.
 *
 * Por isso uma regra MALFORMADA aqui devolve "sem limite" em vez de estourar:
 * no motor ela é um erro de cadastro, mas derrubar a matriz do corretor por
 * causa disso trocaria um limite não checado por uma tela quebrada. O motor
 * recusa do mesmo jeito.
 */

import { centsToBRL } from '@/features/quotes/utils/rd-units.util';

export type RdLimitContext = {
  basicaCents: number;
  equipValueCents: number;
};

/**
 * Multiplica `base` por uma fração decimal em string, com matemática inteira
 * — o mesmo `_frac_times` do motor. `parseFloat` seguido de multiplicação
 * daria 0.1 * 30000000 = 3000000.0000000005 em ponto flutuante.
 */
function fracTimes(fracStr: string, base: number): number {
  const [intPart, decPart = ''] = fracStr.split('.');
  const scale = 10 ** decPart.length;
  const numerator = Number(intPart || '0') * scale + Number(decPart || '0');
  return Math.floor((numerator * base) / scale);
}

/** Resolve o lado direito de uma cláusula `<=` para um valor absoluto. */
function resolveBound(expr: string, ctx: RdLimitContext): number | null {
  if (expr === 'basica') return ctx.basicaCents;
  if (expr === 'value') return ctx.equipValueCents;
  if (expr.endsWith('*basica')) {
    const frac = expr.slice(0, -'*basica'.length);
    if (!/^\d*\.?\d+$/.test(frac)) return null;
    return fracTimes(frac, ctx.basicaCents);
  }
  if (expr.endsWith('*value')) {
    const frac = expr.slice(0, -'*value'.length);
    if (!/^\d*\.?\d+$/.test(frac)) return null;
    return fracTimes(frac, ctx.equipValueCents);
  }
  return null;
}

/** Resolve uma cláusula para o teto que ela impõe, ou null se malformada. */
function clauseBound(clause: string, ctx: RdLimitContext): number | null {
  if (clause.startsWith('max:')) {
    const cents = Number.parseInt(clause.slice('max:'.length), 10);
    return Number.isNaN(cents) ? null : cents;
  }
  if (clause.startsWith('<=')) return resolveBound(clause.slice(2).trim(), ctx);
  return null;
}

/**
 * Teto efetivo da regra em centavos — a cláusula MAIS restritiva —, ou `null`
 * quando não há regra (ou ela é ilegível).
 */
export function resolveLimit(
  rule: string | null | undefined,
  ctx: RdLimitContext
): number | null {
  if (!rule) return null;
  const bounds = rule
    .split('&')
    .map(clause => clauseBound(clause.trim(), ctx))
    .filter((bound): bound is number => bound !== null);
  return bounds.length ? Math.min(...bounds) : null;
}

/** `true` quando `lmiCents` cabe na regra (e não há regra ⇒ cabe). */
export function validateLimit(
  rule: string | null | undefined,
  lmiCents: number,
  ctx: RdLimitContext
): boolean {
  const limit = resolveLimit(rule, ctx);
  return limit === null || lmiCents <= limit;
}

/**
 * LMI da Básica a partir da regra dela — o porte de `limit_rule.derive_basica`.
 *
 * A Básica não é digitada no individual: o motor a deriva do valor do
 * equipamento, e a regra do cadastro diz quanto (`<=1.1*value` = 110% do
 * valor, a regra que absorveu a Despesa de Salvamento). `max:` corta no teto.
 * Sem cláusula de valor a base é o próprio valor — `max:500000000` sozinho
 * segue sendo teto, e não o LMI de todo item.
 *
 * Regra ilegível, ou circular (`basica` na regra da própria Básica, que o
 * motor recusa no cadastro), cai no valor do equipamento: a tela segue
 * mostrando um número, e quem manda continua sendo o motor.
 */
export function deriveBasicaCents(
  rule: string | null | undefined,
  equipValueCents: number
): number {
  const clauses = (rule ?? '')
    .split('&')
    .map(clause => clause.trim())
    .filter(Boolean);
  if (clauses.some(clause => clause.endsWith('basica'))) return equipValueCents;
  const ctx = { basicaCents: equipValueCents, equipValueCents };
  const bounds = clauses
    .map(clause => clauseBound(clause, ctx))
    .filter((bound): bound is number => bound !== null);
  if (!clauses.some(clause => clause.endsWith('value'))) {
    bounds.push(equipValueCents);
  }
  return Math.min(...bounds);
}

/**
 * Legenda da Básica na matriz, lida da MESMA regra que dá o valor dela:
 * "110% do valor do equipamento" com `<=1.1*value`, "igual ao valor do
 * equipamento" sem fração. Quando o teto (`max:`) corta, a legenda diz o
 * limite — senão afirmaria 110% ao lado de um número menor.
 */
export function describeBasicaRule(
  rule: string | null | undefined,
  equipValueCents: number
): string {
  const clauses = (rule ?? '').split('&').map(clause => clause.trim());
  const fracClause = clauses.find(clause => /^<=[\d.]+\*value$/.test(clause));
  const frac = fracClause?.slice(2, -'*value'.length);
  const pct = frac ? fracTimes(frac, 10_000) / 100 : 100;
  const base =
    pct === 100
      ? 'igual ao valor do equipamento'
      : `${String(pct).replace('.', ',')}% do valor do equipamento`;
  const semTeto = frac ? fracTimes(frac, equipValueCents) : equipValueCents;
  const basica = deriveBasicaCents(rule, equipValueCents);
  return basica < semTeto ? `${base}, limitada a ${centsToBRL(basica)}` : base;
}
